'use client';

import { type HTMLAttributes } from 'react';
import { useQuery } from '@tanstack/react-query';
import { type SearchFieldProps } from '../../../components/ui/search-field';
import { cn } from '../../../lib/utils';
import type { AuthUserProfile } from '../../auth/types/auth';
import {
  isAuthenticatedSession,
  useAuthSession,
} from '../../auth/hooks/useAuthSession';
import { AUTH_PROFILE_IMAGE_VIEW_QUERY_KEY } from '../../auth/constants';
import { getProfileImageViewUrl } from '../../auth/api/auth-api';
import { type NotificationTriggerProps } from './notification-trigger';
import {
  UserMenuTrigger,
  type UserMenuTriggerProps,
} from './user-menu-trigger';
import { formatToolbarRole } from '../lib/format-toolbar-role';

function profileDisplayName(user: AuthUserProfile): string {
  const fromName = [user.firstName, user.lastName]
    .filter(Boolean)
    .join(' ')
    .trim();
  return fromName || user.email;
}

export type AppToolbarProps = HTMLAttributes<HTMLElement> & {
  search?: Pick<
    SearchFieldProps,
    | 'placeholder'
    | 'value'
    | 'defaultValue'
    | 'onChange'
    | 'onSubmit'
    | 'className'
    | 'iconClassName'
  >;
  notifications?: Pick<
    NotificationTriggerProps,
    'count' | 'onOpen' | 'aria-label'
  >;
  user?: Pick<
    UserMenuTriggerProps,
    'name' | 'role' | 'avatarSrc' | 'avatarAlt' | 'avatarSize'
  >;
};

/**
 * In-column toolbar (maps to spec **`TopBar`**): **`SearchField`**,
 * **`NotificationTrigger`**, **`UserMenuTrigger`** (menu bodies are out of scope).
 */
export function AppToolbar({
  className,
  search,
  notifications,
  user,
  ...props
}: AppToolbarProps) {
  const { data: session } = useAuthSession();
  const authed = isAuthenticatedSession(session);

  const toolbarUser = authed ? session.data.user : null;

  const profileImageViewQueryKey = [
    ...AUTH_PROFILE_IMAGE_VIEW_QUERY_KEY,
    toolbarUser?.userId ?? 'anon',
  ] as const;

  const profileImageViewQuery = useQuery({
    queryKey: profileImageViewQueryKey,
    queryFn: () => getProfileImageViewUrl(),
    enabled: authed && Boolean(toolbarUser?.profileImageS3Key),
    staleTime: 60_000,
  });

  const profileAvatarSrc =
    toolbarUser?.profileImageS3Key && profileImageViewQuery.data?.signedUrl
      ? profileImageViewQuery.data.signedUrl
      : undefined;

  // const searchFieldProps: Pick<
  //   SearchFieldProps,
  //   | 'placeholder'
  //   | 'value'
  //   | 'defaultValue'
  //   | 'onChange'
  //   | 'onSubmit'
  //   | 'className'
  //   | 'iconClassName'
  // > = {
  //   placeholder: 'Search',
  //   ...search,
  // };

  // const notifCount = notifications?.count ?? 0;
  // const notifOnOpen = notifications?.onOpen ?? (() => {});
  // const notifAria = notifications?.['aria-label'];

  return (
    <header
      className={cn(
        'flex w-full min-w-0 items-center justify-between gap-4 sm:gap-6 lg:gap-8',
        className,
      )}
      {...props}
    >
      {/* <div className='min-w-0 max-w-sm flex-1'>
        <SearchField
          {...searchFieldProps}
          iconClassName={
            searchFieldProps.iconClassName ??
            'text-[var(--color-text-elements-disabled-text-light-mode)]'
          }
          className={cn(
            'h-11 rounded-2xl border-0 bg-white py-0 pr-4 text-sm text-foreground shadow-none placeholder:text-[var(--color-text-elements-disabled-text-light-mode)] sm:pr-5',
            searchFieldProps.className,
          )}
        />
      </div> */}
      {authed ? (
        <div className='flex shrink-0 items-center gap-5 sm:gap-6 ml-auto mr-0'>
          {/* <NotificationTrigger
            count={notifCount}
            onOpen={notifOnOpen}
            {...(notifAria ? { 'aria-label': notifAria } : {})}
          /> */}
          <UserMenuTrigger
            name={user?.name ?? profileDisplayName(session.data.user)}
            role={user?.role ?? formatToolbarRole(session.data.user)}
            avatarSrc={user?.avatarSrc ?? profileAvatarSrc}
            avatarAlt={
              user?.avatarAlt ??
              (profileDisplayName(session.data.user) || 'User profile')
            }
            avatarSize={user?.avatarSize ?? 'md'}
          />
        </div>
      ) : null}
    </header>
  );
}
